import { useState } from 'react'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, Pencil, Trash2, Plane } from 'lucide-react'
import {
  DataTableColumnHeader,
  DataTableFacetedFilter,
  DataTablePagination,
  DataTableToolbar,
} from '@/components/data-table'
import type { AircraftTypeRow, AircraftCategory } from '@/types/aircraft-types'
import { AIRCRAFT_CATEGORY_LABELS, AIRCRAFT_CATEGORIES } from '@/types/aircraft-types'

interface AircraftTypesTableProps {
  data: AircraftTypeRow[]
  isLoading: boolean
  onEdit: (id: string) => void
  onDelete: (id: string, displayName: string) => void
}

/** Category filter options */
const categoryFilterOptions = AIRCRAFT_CATEGORIES.map((cat) => ({
  label: AIRCRAFT_CATEGORY_LABELS[cat],
  value: cat,
  icon: Plane,
}))

/** Status filter options */
const statusFilterOptions = [
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
]

export function AircraftTypesTable({
  data,
  isLoading,
  onEdit,
  onDelete,
}: AircraftTypesTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [globalFilter, setGlobalFilter] = useState('')

  const columns: ColumnDef<AircraftTypeRow>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'manufacturer',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Manufacturer" />
      ),
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('manufacturer')}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'model',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Model" />
      ),
      cell: ({ row }) => {
        const model = row.getValue('model') as string
        const variant = row.original.variant
        return (
          <div>
            <span className="font-medium">{model}</span>
            {variant && (
              <span className="text-muted-foreground ml-1">({variant})</span>
            )}
          </div>
        )
      },
      enableHiding: false,
    },
    {
      accessorKey: 'type_code',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Type Code" />
      ),
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.getValue('type_code')}</span>
      ),
    },
    {
      accessorKey: 'icao_designator',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="ICAO" />
      ),
      cell: ({ row }) => {
        const icao = row.getValue('icao_designator') as string | null
        return icao ? (
          <span className="font-mono text-sm">{icao}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      accessorKey: 'category',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Category" />
      ),
      cell: ({ row }) => {
        const category = row.getValue('category') as AircraftCategory | null
        if (!category) return <span className="text-muted-foreground">—</span>
        return (
          <Badge variant="outline">
            {AIRCRAFT_CATEGORY_LABELS[category] || category}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      id: 'dimensions',
      header: 'Dimensions (ft)',
      cell: ({ row }) => {
        const { wingspan, length, height } = row.original
        return (
          <div className="text-sm">
            <span className="text-muted-foreground">W:</span>
            {wingspan?.toFixed(1) || '—'}
            <span className="text-muted-foreground ml-2">L:</span>
            {length?.toFixed(1) || '—'}
            <span className="text-muted-foreground ml-2">H:</span>
            {height?.toFixed(1) || '—'}
          </div>
        )
      },
    },
    {
      accessorKey: 'model_file_path',
      header: '3D Model',
      cell: ({ row }) => {
        const path = row.getValue('model_file_path') as string | null
        return path ? (
          <Badge variant="default" className="text-xs">
            Has Model
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">
            No Model
          </Badge>
        )
      },
    },
    {
      accessorKey: 'is_active',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const isActive = row.getValue('is_active') as boolean | null
        return (
          <Badge variant={isActive !== false ? 'default' : 'secondary'}>
            {isActive !== false ? 'Active' : 'Inactive'}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        const isActive = row.getValue(id) as boolean | null
        const stringValue = isActive !== false ? 'true' : 'false'
        return value.includes(stringValue)
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const displayName = `${row.original.manufacturer} ${row.original.model}`
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(row.original.id)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDelete(row.original.id, displayName)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      enableHiding: false,
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = filterValue.toLowerCase()
      const manufacturer = (row.original.manufacturer || '').toLowerCase()
      const model = (row.original.model || '').toLowerCase()
      const typeCode = (row.original.type_code || '').toLowerCase()
      const icao = (row.original.icao_designator || '').toLowerCase()
      return (
        manufacturer.includes(search) ||
        model.includes(search) ||
        typeCode.includes(search) ||
        icao.includes(search)
      )
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
  })

  // Export to CSV
  const handleExport = () => {
    const headers = [
      'Manufacturer',
      'Model',
      'Variant',
      'Type Code',
      'ICAO',
      'Category',
      'Wingspan',
      'Length',
      'Height',
      'Has 3D Model',
      'Status',
    ]
    const rows = table.getFilteredRowModel().rows.map((row) => [
      row.original.manufacturer,
      row.original.model,
      row.original.variant || '',
      row.original.type_code,
      row.original.icao_designator || '',
      row.original.category
        ? AIRCRAFT_CATEGORY_LABELS[row.original.category]
        : '',
      row.original.wingspan?.toFixed(1) || '',
      row.original.length?.toFixed(1) || '',
      row.original.height?.toFixed(1) || '',
      row.original.model_file_path ? 'Yes' : 'No',
      row.original.is_active !== false ? 'Active' : 'Inactive',
    ])

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `aircraft_types_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  return (
    <div className="space-y-4">
      {/* Toolbar with search, filters, and view options */}
      <DataTableToolbar
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        filterPlaceholder="Search aircraft types..."
        onExport={handleExport}
        exportLabel="Export CSV"
      >
        <DataTableFacetedFilter
          column={table.getColumn('category')}
          title="Category"
          options={categoryFilterOptions}
        />
        <DataTableFacetedFilter
          column={table.getColumn('is_active')}
          title="Status"
          options={statusFilterOptions}
        />
      </DataTableToolbar>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    Loading aircraft types...
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    No aircraft types found. Add your first aircraft type to get
                    started.
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Enhanced Pagination */}
      <DataTablePagination table={table} />
    </div>
  )
}
